import { CLASS_PLACEMENT_KEYS } from '@schemas'
import { ClubAwardPlaces, givenClubAwards } from '@/components/club-awards'
import { HeroWinner } from '@/components/hero-award'
import { useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { DownloadIcon } from 'lucide-react'
import { useReportSummary, useReportResults, useReportAwards } from '@/api/hooks'
import { SelectShowNotice } from '@/components/current-show'
import { useCurrentShow } from '@/context/current-show'
import { ReportLayout } from '@/layouts/ReportLayout'
import { Button } from '@/components/ui/button'
import { triggerReportPrint } from '@/lib/print'
import { parseDateOnlyToLocal } from '@/lib/date'
import {
  buildDivisionWinners,
  formatClassDisplayName,
  formatDivisionDisplayName,
  getPlacementParts,
  orderPlacementColumns,
  renderAwardWinner,
} from '@/lib/report-results'
import type { ReportAwardsOutput, ReportResultsOutput, ReportSummaryOutput } from '@schemas'

const ANNOUNCEMENT_PLACEMENT_ORDER = [...CLASS_PLACEMENT_KEYS].reverse()

export default function JudgeSpeakingReportPage() {
  const { currentShow } = useCurrentShow()
  const [searchParams] = useSearchParams()
  const showIdParam = searchParams.get('showId') ?? undefined
  const showNameParam = searchParams.get('showName') ?? undefined
  const showLocationParam = searchParams.get('showLocation') ?? undefined
  const showStartDateParam = searchParams.get('showStartDate') ?? undefined
  const showEndDateParam = searchParams.get('showEndDate') ?? undefined

  const showId = currentShow?.id ?? showIdParam
  const showName = currentShow?.name ?? showNameParam ?? 'Selected Show'
  const showLocation = currentShow?.location ?? showLocationParam ?? undefined
  const showStartDate = currentShow?.startDate ?? showStartDateParam ?? undefined
  const showEndDate = currentShow?.endDate ?? showEndDateParam ?? undefined

  const summaryQuery = useReportSummary(showId)
  const resultsQuery = useReportResults(showId)
  const awardsQuery = useReportAwards(showId)

  const isLoading = summaryQuery.isLoading || resultsQuery.isLoading || awardsQuery.isLoading
  const error = summaryQuery.error ?? resultsQuery.error ?? awardsQuery.error ?? null

  const summary: ReportSummaryOutput | undefined = summaryQuery.data
  const results: ReportResultsOutput | undefined = resultsQuery.data
  const awards: ReportAwardsOutput | undefined = awardsQuery.data

  const divisionWinners = useMemo(() => buildDivisionWinners(awards, results), [awards, results])
  const clubAwards = givenClubAwards(awards, showId, currentShow)
  const showDivisionAwards = awards?.bestInDivisionEnabled !== false

  const preparedRange = useMemo(() => {
    if (!showStartDate || !showEndDate) return undefined
    const start = parseDateOnlyToLocal(showStartDate)
    const end = parseDateOnlyToLocal(showEndDate)
    if (!start || !end) return undefined
    const formatter = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' })
    return `${formatter.format(start)} – ${formatter.format(end)}`
  }, [showEndDate, showStartDate])

  if (!showId) {
    return <SelectShowNotice description="Choose a show to print the results announcement." />
  }

  const header = (
    <div className="space-y-1">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1">
          <p className="text-sm uppercase tracking-wide text-muted-foreground">
            Results Announcement
          </p>
          <h1 className="text-3xl font-semibold leading-tight">{showName}</h1>
          {showLocation || preparedRange ? (
            <p className="text-muted-foreground text-sm">
              {showLocation}
              {showLocation && preparedRange ? ' • ' : ''}
              {showLocation ? (preparedRange ?? '') : preparedRange}
            </p>
          ) : null}
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="print-hidden"
          onClick={triggerReportPrint}
        >
          <DownloadIcon className="mr-2 h-4 w-4" aria-hidden="true" />
          Download PDF
        </Button>
      </div>
    </div>
  )

  if (isLoading) {
    return (
      <ReportLayout header={header}>
        <p className="text-muted-foreground text-base">Loading results announcement…</p>
      </ReportLayout>
    )
  }

  if (error) {
    return (
      <ReportLayout header={header}>
        <p className="text-destructive text-base">
          {error.message || 'Unable to load announcement data.'}
        </p>
      </ReportLayout>
    )
  }

  if (!summary || !results || !awards) {
    return (
      <ReportLayout header={header}>
        <p className="text-destructive text-base">Announcement data was not available.</p>
      </ReportLayout>
    )
  }

  return (
    <ReportLayout header={header}>
      <section className="space-y-3">
        <h2 className="text-xl font-semibold">Show Overview</h2>
        <dl className="grid gap-2 sm:grid-cols-3 text-base">
          <div className="flex flex-col">
            <dt className="text-sm font-medium text-muted-foreground">Entrants</dt>
            <dd className="text-lg font-semibold">{summary.entrants}</dd>
          </div>
          <div className="flex flex-col">
            <dt className="text-sm font-medium text-muted-foreground">Entries</dt>
            <dd className="text-lg font-semibold">{summary.entries}</dd>
          </div>
          <div className="flex flex-col">
            <dt className="text-sm font-medium text-muted-foreground">Black Jar Entries</dt>
            <dd className="text-lg font-semibold">{summary.blackJarEntries}</dd>
          </div>
        </dl>
      </section>

      <section className="space-y-4">
        <h2 className="text-xl font-semibold report-layout__section-heading">Division Results</h2>
        {results.divisions.length === 0 ? (
          <p className="text-muted-foreground">No judging results have been recorded.</p>
        ) : (
          <div className="space-y-6">
            {results.divisions.map(division => (
              <article
                key={division.divisionId}
                className="space-y-3 break-inside-avoid division-section"
              >
                <header>
                  <h3 className="text-lg font-semibold report-layout__subheading division-heading">
                    {formatDivisionDisplayName(division.divisionName, division.divisionCode)}
                  </h3>
                </header>
                <div className="report-columns">
                  {division.classes.map(showClass => {
                    const placementColumns = orderPlacementColumns(showClass.placements, {
                      order: ANNOUNCEMENT_PLACEMENT_ORDER,
                      placeCount: showClass.placeCount,
                    }).filter(
                      column =>
                        ['1st', '2nd', '3rd'].includes(column.label) || column.entries.length > 0
                    )
                    const classDisplayName = formatClassDisplayName(
                      showClass.className,
                      showClass.classCode
                    )
                    return (
                      <article key={showClass.classId} className="report-columns__item">
                        <h4 className="report-columns__class-heading">
                          <span className="class-name">{classDisplayName}</span>
                        </h4>
                        {placementColumns.length === 0 ? (
                          <p className="text-sm text-muted-foreground">
                            No placements recorded for this class.
                          </p>
                        ) : (
                          <div className="report-columns__placements">
                            {placementColumns.map(column => {
                              const { base, suffix } = getPlacementParts(column.label)
                              const renderLabel = () => (
                                <span className="report-columns__placement-label">
                                  <span>{base}</span>
                                  {suffix ? <sup>{suffix}</sup> : null}
                                </span>
                              )
                              return (
                                <div key={column.label} className="report-columns__placement-group">
                                  {column.entries.length === 0 ? (
                                    <div
                                      className="report-columns__placement-row"
                                      key={`${column.label}-placeholder`}
                                    >
                                      {renderLabel()}{' '}
                                      <span
                                        className="report-columns__placement-separator"
                                        aria-hidden="true"
                                      >
                                        -
                                      </span>{' '}
                                      <span className="report-columns__placement-name">-</span>
                                    </div>
                                  ) : (
                                    column.entries.map((entry, entryIndex) => {
                                      const exhibitor =
                                        entry.exhibitorName?.trim() || 'Unknown exhibitor'
                                      const key = entry.exhibitId
                                        ? `${column.label}-${entry.exhibitId}`
                                        : `${column.label}-${entry.exhibitorId ?? exhibitor}-${entryIndex}`
                                      return (
                                        <div key={key} className="report-columns__placement-row">
                                          {renderLabel()}{' '}
                                          <span
                                            className="report-columns__placement-separator"
                                            aria-hidden="true"
                                          >
                                            -
                                          </span>{' '}
                                          <span className="report-columns__placement-name">
                                            {exhibitor}
                                          </span>
                                        </div>
                                      )
                                    })
                                  )}
                                </div>
                              )
                            })}
                          </div>
                        )}
                      </article>
                    )
                  })}
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      <section className="space-y-4">
        <h2 className="text-xl font-semibold report-layout__section-heading">Awards Spotlight</h2>
        <div className="space-y-3 text-sm">
          {clubAwards.map(({ title, measure, award }) => (
            <div key={title}>
              <h3 className="font-semibold text-base report-layout__subheading">{title}</h3>
              <ClubAwardPlaces award={award} measure={measure} />
            </div>
          ))}

          {showDivisionAwards ? (
            <div className="space-y-2">
              <h3 className="font-semibold text-base report-layout__subheading">
                Best in Division
              </h3>
              {divisionWinners.length === 0 ? (
                <p>No division champions have been identified.</p>
              ) : (
                <ul className="space-y-1">
                  {divisionWinners.map(item => {
                    const divisionDisplayName = formatDivisionDisplayName(
                      item.divisionName,
                      item.divisionCode
                    )
                    return (
                      <li key={item.divisionId}>
                        <span className="font-medium">{divisionDisplayName}:</span>{' '}
                        {renderAwardWinner(item.winner)}
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>
          ) : null}

          <HeroWinner state={awards?.heroAward} />
          <div>
            <h3 className="font-semibold text-base report-layout__subheading">Best in Show</h3>
            <p>{renderAwardWinner(awards.bestInShow)}</p>
          </div>

          <div>
            <h3 className="font-semibold text-base report-layout__subheading">Sweepstakes</h3>
            <p>{renderAwardWinner(awards.sweepstakes)}</p>
          </div>
          {(awards.reserveGrands ?? []).length > 0 && (
            <div>
              <h3 className="font-semibold text-base report-layout__subheading">
                Reserve Grand Champion
              </h3>
              {awards.reserveGrands?.map((winner, index) => (
                <p key={winner.awardId ?? index}>{renderAwardWinner(winner)}</p>
              ))}
            </div>
          )}
        </div>
      </section>

      <p className="text-sm text-muted-foreground">
        Prepared for {showName}
        {showLocation ? ` • ${showLocation}` : ''}
        {preparedRange ? ` • ${preparedRange}` : ''}
      </p>
    </ReportLayout>
  )
}
